import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { templateQuestionTypeLabel } from '@/features/instruments/templates/lib/templateQuestionTypeLabel'
import { questionListIconTile } from '@/features/instruments/question-builder/questionListRowIconTile'
import { QUESTION_TYPE_CATALOG } from '@/features/instruments/question-builder/questionTypeCatalog'
import type { QuestionTypeId } from '@/features/instruments/question-builder/types'

const API_TYPE_TO_BUILDER: Record<string, QuestionTypeId> = {
  multiple_choice: 'MULTIPLE_CHOICE',
  picture_choice: 'PICTURE_CHOICE',
  ranking: 'RANKING',
  yes_no: 'YES_NO',
  checkbox: 'CHECKBOX',
  dropdown: 'DROPDOWN',
  short_text: 'SHORT_TEXT',
  long_text: 'LONG_TEXT',
  date: 'DATE',
  rating: 'RATING',
  likert: 'LIKERT',
  slider: 'SLIDER',
}

/** Figma 6074:13382 — Question Bank card badge tints (dev handoff). */
const QUESTION_BANK_CARD_BADGE: Partial<
  Record<QuestionTypeId, { tileBg: string; iconColor: string }>
> = {
  MULTIPLE_CHOICE: { tileBg: '#ccfbf1', iconColor: '#0d9488' },
}

type Props = Readonly<{
  apiType: string
  compact?: boolean
}>

export function QuestionBankTypeBadge({ apiType, compact = false }: Props) {
  const builderId = API_TYPE_TO_BUILDER[apiType]
  const catalogEntry = builderId
    ? QUESTION_TYPE_CATALOG.find((e) => e.id === builderId)
    : undefined
  const Icon = catalogEntry?.Icon
  const tileOverride = builderId ? QUESTION_BANK_CARD_BADGE[builderId] : undefined
  const tile = builderId ? questionListIconTile(builderId) : null
  const tileBg = tileOverride?.tileBg ?? tile?.tileBg ?? '#eaf3ff'
  const iconColor = tileOverride?.iconColor ?? tile?.iconColor ?? '#008cd0'
  const label = templateQuestionTypeLabel(apiType)

  if (compact) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          height: '32px',
          boxSizing: 'border-box',
          px: '10px',
          borderRadius: '8px',
          bgcolor: tileBg,
          maxWidth: '100%',
          flexShrink: 0,
        }}
        data-testid="question-bank-type-badge"
      >
        {Icon ? <Icon size={14} color={iconColor} aria-hidden /> : null}
        <Typography
          component="span"
          sx={{
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.28px',
            color: iconColor,
            fontWeight: 400,
            textTransform: 'capitalize',
            whiteSpace: 'nowrap',
            m: 0,
          }}
        >
          {label}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        px: '10px',
        py: '6px',
        borderRadius: '8px',
        bgcolor: tileBg,
        maxWidth: '100%',
      }}
      data-testid="question-bank-type-badge"
    >
      {Icon ? <Icon size={14} color={iconColor} aria-hidden /> : null}
      <Typography
        component="span"
        sx={{
          fontSize: '14px',
          lineHeight: '20px',
          color: iconColor,
          fontWeight: 400,
          textTransform: 'capitalize',
          whiteSpace: 'nowrap',
          m: 0,
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}
